/**
 * Auth service layer.
 *
 * THE ONLY FILE that imports from src/lib/supabase.ts for auth purposes.
 * Components, hooks, and stores must import from this file — never from supabase.ts directly.
 * This keeps the migration to NestJS + Postgres cheap: swap this file only.
 */

import { supabase } from "../supabase";
import type {
  AuthResult,
  AuthSession,
  AuthUser,
  AuthError,
  AuthErrorCode,
} from "../../types/auth";

// ─── Internal helpers ────────────────────────────────────────────────────────

function mapSupabaseError(error: unknown): AuthError {
  if (!error || typeof error !== "object") {
    return { code: "unknown", message: "An unknown error occurred" };
  }

  const err = error as { message?: string; status?: number; code?: string };
  const msg = err.message ?? "";
  const status = err.status ?? 0;

  // Network-level failures
  if (
    msg.toLowerCase().includes("fetch") ||
    msg.toLowerCase().includes("network") ||
    msg.toLowerCase().includes("failed to fetch") ||
    status === 0
  ) {
    return { code: "network", message: "Connection problem — check your network and try again" };
  }

  // Weak password — check before email_taken because both can come back as 422
  if (
    msg.toLowerCase().includes("password should be") ||
    msg.toLowerCase().includes("at least") ||
    msg.toLowerCase().includes("weak password")
  ) {
    return { code: "weak_password", message: "Must be at least 8 characters" };
  }

  // Email already registered
  if (
    msg.toLowerCase().includes("already registered") ||
    msg.toLowerCase().includes("user already registered") ||
    msg.toLowerCase().includes("email already in use") ||
    status === 422
  ) {
    return { code: "email_taken", message: "Already registered. Sign in instead." };
  }

  // Wrong credentials
  if (
    msg.toLowerCase().includes("invalid login") ||
    msg.toLowerCase().includes("invalid credentials") ||
    msg.toLowerCase().includes("wrong password") ||
    status === 400
  ) {
    return { code: "invalid_credentials", message: "Wrong password" };
  }

  // Email not verified
  if (
    msg.toLowerCase().includes("email not confirmed") ||
    msg.toLowerCase().includes("not verified")
  ) {
    return { code: "unverified", message: "Check your inbox to verify your email." };
  }

  return { code: "unknown", message: msg || "An unexpected error occurred" };
}

function mapSupabaseUser(
  user: { id: string; email?: string | null; email_confirmed_at?: string | null } | null,
): AuthUser | null {
  if (!user || !user.email) return null;
  return {
    id: user.id,
    email: user.email,
    emailVerified: user.email_confirmed_at != null,
  };
}

function mapSupabaseSession(
  session: {
    access_token: string;
    expires_at?: number | null;
    user: { id: string; email?: string | null; email_confirmed_at?: string | null };
  } | null,
): AuthSession | null {
  if (!session) return null;
  const user = mapSupabaseUser(session.user);
  if (!user) return null;
  return {
    user,
    accessToken: session.access_token,
    expiresAt: session.expires_at ?? 0,
  };
}

// ─── Public API ──────────────────────────────────────────────────────────────

export interface SignUpParams {
  email: string;
  password: string;
}

/**
 * Create a new account. On success, Supabase sends a verification email.
 * Returns the user (not yet a full session — email must be verified first).
 */
export async function signUp({
  email,
  password,
}: SignUpParams): Promise<AuthResult<AuthUser>> {
  try {
    // Force the confirmation-email link to use the current origin
    // (prod domain on prod, localhost on local). Without this,
    // Supabase falls back to the "Site URL" in the dashboard which
    // defaults to localhost for the dev project and would send users
    // a link that doesn't open the actual app.
    //
    // NOTE: the origin also needs to be in the dashboard's allowlist
    // (Authentication → URL Configuration → Redirect URLs) or
    // Supabase silently ignores this option.
    const emailRedirectTo =
      typeof window !== "undefined" ? `${window.location.origin}/verify` : undefined;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: emailRedirectTo ? { emailRedirectTo } : undefined,
    });

    if (error) {
      return { ok: false, error: mapSupabaseError(error) };
    }

    const user = mapSupabaseUser(data.user);
    if (!user) {
      return {
        ok: false,
        error: { code: "unknown", message: "Signup succeeded but no user was returned" },
      };
    }

    return { ok: true, data: user };
  } catch (err) {
    return { ok: false, error: mapSupabaseError(err) };
  }
}

export interface SignInParams {
  email: string;
  password: string;
}

/**
 * Sign in with email + password.
 * Returns a full session on success.
 */
export async function signIn({
  email,
  password,
}: SignInParams): Promise<AuthResult<AuthSession>> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return { ok: false, error: mapSupabaseError(error) };
    }

    const session = mapSupabaseSession(data.session);
    if (!session) {
      return {
        ok: false,
        error: { code: "unverified", message: "Check your inbox to verify your email." },
      };
    }

    return { ok: true, data: session };
  } catch (err) {
    return { ok: false, error: mapSupabaseError(err) };
  }
}

/**
 * Sign out the current user.
 */
export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
}

/**
 * Get the current active session, or null if not signed in.
 */
export async function getSession(): Promise<AuthSession | null> {
  const { data } = await supabase.auth.getSession();
  return mapSupabaseSession(data.session);
}

/**
 * Resend the verification email for the given address.
 */
export async function resendVerification(
  email: string,
): Promise<AuthResult<void>> {
  try {
    // Same story as signUp: bind the email link to the current origin
    // so resend from prod doesn't accidentally hand out a localhost URL.
    const emailRedirectTo =
      typeof window !== "undefined" ? `${window.location.origin}/verify` : undefined;

    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: emailRedirectTo ? { emailRedirectTo } : undefined,
    });

    if (error) {
      return { ok: false, error: mapSupabaseError(error) };
    }

    return { ok: true, data: undefined };
  } catch (err) {
    return { ok: false, error: mapSupabaseError(err) };
  }
}

/**
 * Subscribe to auth state changes.
 * Returns an unsubscribe function. Call it in useEffect cleanup.
 *
 * Usage in a hook or component:
 *   const unsub = onAuthStateChange((session) => { ... });
 *   return () => unsub();
 */
export function onAuthStateChange(
  callback: (session: AuthSession | null) => void,
): () => void {
  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(mapSupabaseSession(session));
  });

  return () => subscription.unsubscribe();
}

// ─── A-02 / A-03 OAuth providers ────────────────────────────────────────────

export type OAuthProvider = "google" | "apple";

/**
 * Start an OAuth flow. Redirects the browser to the provider. The
 * returned Promise resolves with the OAuth URL (Supabase also kicks
 * off the redirect in most browsers).
 *
 * Works only after Java configures the provider in Supabase Dashboard
 * → Authentication → Providers. Until then, signInWithOAuth errors
 * with "provider not enabled" which maps to code="unknown".
 */
export async function signInWithOAuth(
  provider: OAuthProvider,
): Promise<AuthResult<{ url: string }>> {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/app`,
      },
    });
    if (error) return { ok: false, error: mapSupabaseError(error) };
    return { ok: true, data: { url: data.url ?? "" } };
  } catch (err) {
    return { ok: false, error: mapSupabaseError(err) };
  }
}

// ─── A-04 Password reset ────────────────────────────────────────────────────

/**
 * Send a password-reset email. The email link lands on
 * /reset-password with an access_token query param that Supabase's
 * client picks up automatically on page load.
 */
export async function requestPasswordReset(
  email: string,
): Promise<AuthResult<void>> {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) return { ok: false, error: mapSupabaseError(error) };
    return { ok: true, data: undefined };
  } catch (err) {
    return { ok: false, error: mapSupabaseError(err) };
  }
}

/**
 * Set a new password for the currently-recovering user. Called from
 * the /reset-password page after the user arrives from the email link
 * (Supabase has already populated the recovery session).
 */
export async function updatePassword(
  newPassword: string,
): Promise<AuthResult<void>> {
  try {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return { ok: false, error: mapSupabaseError(error) };
    return { ok: true, data: undefined };
  } catch (err) {
    return { ok: false, error: mapSupabaseError(err) };
  }
}

// Re-export the error code type so consumers can switch on it
export type { AuthErrorCode };
